<!--
  History. Four months of imported sessions plus whatever he logs from now on.

  Deliberately a flat list with no graphs: the plan is explicit that the home
  screen gets no charts and no streaks.
-->
<script lang="ts">
  import { app } from '../lib/state.svelte';
  import { api } from '../lib/api';
  import { duration, kg, whenShort } from '../lib/format';
  import type { PerformedSet, SessionOverview } from '../lib/types';

  let openId = $state<string | null>(null);
  let sets = $state<PerformedSet[]>([]);
  let loadingSets = $state(false);

  async function toggle(session: SessionOverview) {
    if (openId === session.id) {
      openId = null;
      sets = [];
      return;
    }
    openId = session.id;
    sets = [];
    loadingSets = true;
    try {
      const res = await api.get<{ sets: PerformedSet[] }>(`/api/sessions/${session.id}`);
      sets = res.sets;
    } catch {
      sets = [];
    } finally {
      loadingSets = false;
    }
  }

  const grouped = $derived.by(() => {
    const out = new Map<string, PerformedSet[]>();
    for (const s of sets) {
      const name = app.exerciseById.get(s.exercise_id)?.name ?? 'Unknown';
      if (!out.has(name)) out.set(name, []);
      out.get(name)!.push(s);
    }
    return [...out.entries()];
  });

  const EFFORT = ['', 'easy', 'hard', 'failed'];

  // Imported sets carry no effort and never will, so say so rather than
  // showing a row of blanks.
  const predatesEffort = $derived(sets.length > 0 && sets.every((r) => r.pre_effort_tracking));
</script>

<div class="history">
  <header>
    <button class="link" onclick={() => app.go('home')}>&larr; Back</button>
    <span class="count">{app.history.length} sessions</span>
  </header>

  {#if app.historyLoading && app.history.length === 0}
    <p class="muted">Loading…</p>
  {:else if app.history.length === 0}
    <p class="muted">Nothing here yet.</p>
  {/if}

  <ul class="sessions">
    {#each app.history as s (s.id)}
      <li class:ghost={s.is_ghost}>
        <button class="row" onclick={() => toggle(s)}>
          <span class="when">{whenShort(s.started_at)}</span>
          <span class="mid">
            <span class="day">{s.day_name ?? 'Session'}</span>
            <span class="meta">
              {#if s.is_ghost}
                opened, nothing logged
              {:else}
                {s.completed_set_count} sets
                {#if s.volume_kg}· {kg(s.volume_kg)} kg{/if}
                {#if s.duration_seconds}· {duration(s.duration_seconds)}{/if}
              {/if}
            </span>
          </span>
          {#if s.source === 'import'}<span class="tag">old</span>{/if}
        </button>

        {#if openId === s.id}
          <div class="detail">
            {#if loadingSets}
              <p class="muted small">Loading sets…</p>
            {:else if grouped.length === 0}
              <p class="muted small">No sets, or offline.</p>
            {:else}
              {#each grouped as [name, rows] (name)}
                <div class="exercise">
                  <span class="ex-name">{name}</span>
                  <span class="ex-sets">
                    {#each rows as r, i (r.id)}{i > 0 ? ' · ' : ''}{#if !r.completed}<span
                          class="skip">skipped{r.skip_reason ? ` (${r.skip_reason})` : ''}</span
                        >{:else}{r.time_seconds != null
                          ? `${r.time_seconds}s`
                          : `${r.reps ?? '-'}${r.weight_kg != null ? `×${kg(r.weight_kg)}` : ''}`}{#if r.effort}<span
                            class="effort">{EFFORT[r.effort]}</span
                          >{/if}{/if}{/each}
                  </span>
                </div>
              {/each}
              {#if predatesEffort}
                <p class="muted small">
                  Logged before the app tracked effort - no easy/hard/failed on these.
                </p>
              {/if}
            {/if}
          </div>
        {/if}
      </li>
    {/each}
  </ul>
</div>

<style>
  .history {
    min-height: 100dvh;
    padding: env(safe-area-inset-top) 12px calc(env(safe-area-inset-bottom) + 24px);
  }

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 4px;
  }

  .count {
    color: var(--dim);
    font-size: 0.85rem;
  }

  .link {
    background: none;
    border: 0;
    color: var(--dim);
    font-size: 0.95rem;
    padding: 8px 4px;
  }

  .sessions {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .sessions li {
    border-bottom: 1px solid var(--line);
  }

  .row {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    background: none;
    border: 0;
    color: var(--text);
    padding: 14px 4px;
    text-align: left;
  }

  .when {
    color: var(--dim);
    font-size: 0.8rem;
    min-width: 5.5rem;
  }

  .mid {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
  }

  .day {
    font-size: 0.98rem;
  }

  .meta {
    color: var(--dim);
    font-size: 0.82rem;
  }

  .ghost .day {
    color: var(--dim);
  }

  .tag {
    color: var(--dim);
    border: 1px solid var(--line);
    border-radius: 999px;
    font-size: 0.7rem;
    padding: 2px 8px;
  }

  .detail {
    padding: 4px 4px 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .exercise {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .ex-name {
    font-size: 0.88rem;
  }

  .ex-sets {
    color: var(--dim);
    font-size: 0.82rem;
    font-variant-numeric: tabular-nums;
  }

  .effort {
    color: var(--accent-text);
    font-size: 0.7rem;
    margin-left: 4px;
  }

  .skip {
    color: var(--failed);
  }

  .muted {
    color: var(--dim);
    text-align: center;
    padding: 20px;
  }

  .small {
    font-size: 0.82rem;
    padding: 6px 0;
    text-align: left;
  }
</style>
